import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Paper
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { DisplaySettings as DisplaySettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';
import ThemeSettings from './ThemeSettings';
import BannerSettings from './BannerSettings';
import PromotionSettings from './PromotionSettings';
import VisualThemeEditor from './VisualThemeEditor';
import MobileThemeEditor from './MobileThemeEditor';

interface DisplaySettingsProps {
  settings: DisplaySettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`display-settings-tabpanel-${index}`}
      aria-labelledby={`display-settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DisplaySettings: React.FC<DisplaySettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<DisplaySettingsType>(settings);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // تحديث البيانات المحلية عند تغيير الإعدادات الخارجية
  useEffect(() => {
    setFormData(settings);
  }, [settings]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    try {
      onSave(true);
      onError(null);

      await updateSection('display', formData);

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      onError('حدث خطأ أثناء حفظ إعدادات العرض');
    } finally {
      onSave(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          إعدادات العرض
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => {
              console.log('Edit button clicked, setting editing to true');
              setEditing(true);
            }}
            variant="outlined"
          >
            تعديل
          </Button>
        ) : (
          <Button
            onClick={() => handleSubmit()}
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            حفظ التغييرات
          </Button>
        )}
      </Box>

      {/* زر إضافي لتفعيل التحرير مباشرة */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={() => {
            console.log('Force edit button clicked');
            setEditing(true);
          }}
        >
          انقر هنا لتفعيل التحرير مباشرة
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* إعدادات ظهور البانر والإعلانات */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          إعدادات ظهور العناصر
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.showBanners !== false}
                onChange={(e) => {
                  const newFormData = { ...formData, showBanners: e.target.checked };
                  setFormData(newFormData);
                }}
                disabled={!editing}
              />
            }
            label="إظهار البانر العلوي"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.showPromotions !== false}
                onChange={(e) => {
                  const newFormData = { ...formData, showPromotions: e.target.checked };
                  setFormData(newFormData);
                }}
                disabled={!editing}
              />
            }
            label="إظهار الإعلانات"
          />
        </Box>
      </Paper>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        aria-label="display settings tabs"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="الثيمات" />
        <Tab label="البانر" />
        <Tab label="الإعلانات" />
        <Tab label="التخصيص المرئي (Visual Editor)" />
        <Tab label="تخصيص الجوال (Mobile Editor)" />
      </Tabs>

      <TabPanel value={activeTab} index={0}>
        <ThemeSettings
          settings={settings}
          onSave={onSave}
          onError={onError}
          onSuccess={onSuccess}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <BannerSettings
          settings={settings}
          onSave={onSave}
          onError={onError}
          onSuccess={onSuccess}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <PromotionSettings
          settings={settings}
          onSave={onSave}
          onError={onError}
          onSuccess={onSuccess}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <VisualThemeEditor
          settings={settings}
          onSave={onSave}
          onError={onError}
          onSuccess={onSuccess}
        />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <MobileThemeEditor
          settings={settings}
          onSave={onSave}
          onError={onError}
          onSuccess={onSuccess}
        />
      </TabPanel>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setEditing(false)}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>
          <Button
            onClick={() => handleSubmit()}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default DisplaySettings;
